/**
 * TelemetryClient — Sistema de telemetría de sesiones
 * Registra eventos del funnel de conversión sin datos personales
 */

export class TelemetryClient {
  private static instance: TelemetryClient;
  private sessionId: string;
  private lang: string;
  private currency: string;
  private endpoint: string = '/action/telemetria/';
  private eventLog: any[] = []; // Registro de todos los eventos
  private deviceInfo: { device: string; browser: string; os: string };
  private erroresVistos = new Set<string>(); // Dedup de errores por sesión
  private erroresEnviados = 0; // Tope de errores por sesión

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.lang = this.extractLang();
    this.currency = this.extractCurrency();
    this.deviceInfo = this.parseDeviceInfo();
    // Registrar listener global de errores
    this.setupGlobalErrorHandling();
  }

  /**
   * Configura handlers globales para capturar TODOS los errores JS accionables.
   * Filosofía: registrar todo lo que sea nuestro código o accionable (con el máximo
   * de data para llegar al origen) y descartar solo el ruido externo intocable:
   * scripts de terceros cross-origin y fetch cancelado por navegación/sin conexión.
   */
  private setupGlobalErrorHandling(): void {
    // Errores JS + fallos de carga de recursos. Usamos captura (true) porque los
    // errores de recurso (script/css que no cargan) NO burbujean: solo se ven en captura.
    window.addEventListener('error', (event: ErrorEvent) => {
      const target: any = event.target;

      // 1) Fallo de carga de un recurso (no es un throw de JS): target es el elemento.
      if (target && target !== window && target.tagName) {
        // Solo SCRIPT/LINK propios: un chunk JS o CSS nuestro que no carga (deploy viejo,
        // 404 de bundle) es muy accionable. Assets de terceros que fallan no los tocamos.
        if (target.tagName !== 'SCRIPT' && target.tagName !== 'LINK') return;
        const url = target.src || target.href || '';
        if (!url || !this.esUrlPropia(url)) return;
        // Chunk propio que no carga = deploy nuevo en servidor, HTML viejo en caché del usuario.
        // Recargamos una vez. Durante el cooldown de 60s suprimimos también: son la misma
        // ráfaga paralela del mismo deploy, no errores independientes.
        if (/\/public\/js\//.test(url)) {
          if (!this.intentarReloadPorChunk()) {
            this.reportarError('recurso', `No cargó ${target.tagName.toLowerCase()}: ${url}`, { recurso: url });
          }
          return;
        }
        this.reportarError('recurso', `No cargó ${target.tagName.toLowerCase()}: ${url}`, { recurso: url });
        return;
      }

      // 2) Error JS real
      const stack = (event.error as any)?.stack || null;
      const isScriptError = !event.filename && !event.lineno && !event.colno;
      // "Script error." sin stack = script de tercero cross-origin sin CORS. Intocable.
      if (isScriptError && !stack) return;

      this.reportarError('js', event.message || 'Error desconocido', {
        nombre: (event.error as any)?.name || null,
        filename: event.filename || null,
        lineno: event.lineno || null,
        colno: event.colno || null,
        stack,
      });
    }, true);

    // Promesas rechazadas no manejadas
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const r: any = event.reason;
      const mensaje = r instanceof Error ? r.message : (typeof r === 'string' ? r : 'Promesa rechazada');
      const stack = r instanceof Error ? (r.stack || null) : null;

      if (this.esRuidoExterno(mensaje, stack)) return;

      // ChunkLoadError = import dinámico falló porque el chunk ya no existe en servidor.
      // Recargamos una vez. Durante el cooldown suprimimos también: misma ráfaga del deploy.
      if (r instanceof Error && r.name === 'ChunkLoadError') {
        if (!this.intentarReloadPorChunk()) {
          this.reportarError('promise', mensaje || 'ChunkLoadError sin reload (cooldown)', {
            nombre: 'ChunkLoadError',
            stack: stack || null,
          });
        }
        return;
      }

      this.reportarError('promise', mensaje || 'Promesa rechazada sin manejo', {
        nombre: r instanceof Error ? r.name : null,
        stack,
      });
    });
  }

  /**
   * Recarga la página una sola vez cuando un chunk propio falla (deploy nuevo en servidor,
   * HTML viejo en caché del usuario). sessionStorage actúa de guard: si ya recargamos en
   * los últimos 60 s y el problema persiste, lo reportamos como error normal en lugar de
   * bucle infinito.
   * Retorna true si se va a recargar (el caller debe abortar el flujo de reporte).
   */
  private intentarReloadPorChunk(): boolean {
    const KEY = 'alk_chunk_reload';
    const ultimo = parseInt(sessionStorage.getItem(KEY) || '0', 10);
    if (Date.now() - ultimo < 60_000) return false; // ya recargamos hace poco, no insistir
    sessionStorage.setItem(KEY, String(Date.now()));
    window.location.reload();
    return true;
  }

  /** ¿La URL pertenece a nuestro propio origen? */
  private esUrlPropia(url: string): boolean {
    try { return new URL(url, location.href).origin === location.origin; }
    catch { return false; }
  }

  /** ¿El stack apunta a NUESTRO código compilado? (bundles propios) */
  private esNuestroStack(stack: string | null): boolean {
    return !!stack && /\/public\/js\//.test(stack);
  }

  /**
   * Ruido externo intocable: mensajes de red genéricos (fetch cancelado por el usuario,
   * sin conexión, o scripts de terceros) que NO nacen de nuestro código. Los fallos de
   * import dinámico / chunks SÍ se conservan: son accionables (deploy viejo en el cliente).
   */
  private esRuidoExterno(mensaje: string, stack: string | null): boolean {
    if (this.esNuestroStack(stack)) return false; // es nuestro → siempre vale
    const m = (mensaje || '').toLowerCase();
    if (m.includes('dynamically imported') || m.includes('chunk') || m.includes('importing a module')) {
      return false; // fallo de carga de módulo: accionable
    }
    return m.includes('failed to fetch')            // Chrome: fetch caído/cancelado
      || m.includes('load failed')                 // Safari: fetch caído/cancelado
      || m.includes('networkerror')                // Firefox
      || m.includes('cancel')                      // cancelled / canceled
      || m.includes('aborted')
      || m.includes('object not found matching id') // Grammarly extension
      || m.includes('java object is gone')          // Android WebView injection
      || m.includes('webkit.messagehandlers')       // iOS WebKit bridge (apps nativas)
      || m.includes('__firefox__')                  // Firefox internal
      || m.includes('ethereum')                     // MetaMask / wallets crypto
      || m === 'undefined' || m === 'null' || m === '[object object]';
  }

  /**
   * Envía un error deduplicado y con tope por sesión: captura todo sin inundar.
   */
  private reportarError(tipo: 'js' | 'promise' | 'recurso', mensaje: string, contexto: any = {}): void {
    if (this.erroresEnviados >= 30) return; // tope de seguridad por sesión

    // Dedup por huella: mismo tipo+mensaje+primer frame del stack una sola vez
    const primerFrame = (contexto.stack || contexto.filename || '').toString().split('\n')[1] || '';
    const huella = `${tipo}|${mensaje}|${primerFrame}`;
    if (this.erroresVistos.has(huella)) return;
    this.erroresVistos.add(huella);
    this.erroresEnviados++;

    this.send('error', {
      evento_origen: this.getCurrentPageName(),
      error_tipo: tipo,
      error_mensaje: mensaje,
      contexto: { url: window.location.href, ...contexto }
    });
  }

  /**
   * Detecta qué página está cargada actualmente
   */
  private getCurrentPageName(): string {
    const path = window.location.pathname;

    if (path.includes('/landing')) return 'landing';
    if (path.includes('/paso2') || path.includes('/busqueda')) return 'paso2';
    if (path.includes('/reserva-confirmar') || path.includes('/paso3')) return 'paso3';
    if (path.includes('/pago')) return 'pago';
    if (path === '/' || path === '') return 'home';

    return path || 'pagina_desconocida';
  }

  static getInstance(): TelemetryClient {
    if (!TelemetryClient.instance) {
      TelemetryClient.instance = new TelemetryClient();
    }
    return TelemetryClient.instance;
  }

  private generateSessionId(): string {
    // Usar formato ISO (YYYY-MM-DD) para coincidir con la clave que genera PHP
    const today = new Date().toISOString().split('T')[0];
    const storageKey = 'telemetry_session_' + today;

    // Verificar primero la cookie (PHP puede haberla generado en una página server-rendered)
    const cookieMatch = document.cookie.match(new RegExp('(?:^|; )' + storageKey + '=([^;]*)'));
    if (cookieMatch) {
      return decodeURIComponent(cookieMatch[1]);
    }

    // Si ya existe una session_id para HOY en localStorage, reutilizarla
    // (la misma durante toda la sesión del usuario, incluso al cambiar de página)
    const existingId = localStorage.getItem(storageKey);
    if (existingId) {
      return existingId;
    }

    // Generar nueva session_id (solo una vez por día)
    const entropy = `${today}-${Math.random()}-${Date.now()}`;
    let hash = 0;
    for (let i = 0; i < entropy.length; i++) {
      hash = ((hash << 5) - hash) + entropy.charCodeAt(i);
      hash = hash & hash;
    }
    const newId = Math.abs(hash).toString(36);

    // Guardar en localStorage para que sea el MISMO en todas las páginas
    try {
      localStorage.setItem(storageKey, newId);
    } catch (e) {
      // Si localStorage no funciona, continuar sin guardar
    }

    return newId;
  }

  private extractLang(): string {
    return (window as any).alk_lang || navigator.language.split('-')[0] || 'es';
  }

  private extractCurrency(): string {
    const match = document.cookie.match(/currency=([^;]*)/);
    return match ? match[1] : 'COP';
  }

  /**
   * Parsea el user agent para extraer dispositivo, navegador y SO
   */
  private parseDeviceInfo(): { device: string; browser: string; os: string } {
    const ua = navigator.userAgent;
    let device = 'desktop';
    let browser = 'unknown';
    let os = 'unknown';

    // Detectar SO
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

    // Detectar dispositivo
    if (/Mobile|Android|iPhone|iPad|iPod/.test(ua)) {
      device = /iPad/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua)) ? 'tablet' : 'mobile';
    }

    // Detectar navegador
    if (ua.includes('Chrome') && !ua.includes('Chromium')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edge')) browser = 'Edge';
    else if (ua.includes('Opera')) browser = 'Opera';
    else if (ua.includes('Trident')) browser = 'IE';

    return { device, browser, os };
  }

  public send(eventName: string, data: any = {}, currencyOverride?: string): void {
    const timestamp = new Date().toISOString();

    const payload = {
      session_id: this.sessionId,
      event: eventName,
      timestamp: timestamp,
      lang: this.lang,
      currency: currencyOverride ?? this.currency,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      platform: 'web',
      device: this.deviceInfo.device,
      browser: this.deviceInfo.browser,
      os: this.deviceInfo.os,
      data: data
    };

    // Guardar en log interno
    this.eventLog.push({
      num: this.eventLog.length + 1,
      event: eventName,
      timestamp: timestamp,
      data: data,
      status: 'enviando'
    });
    // Capturar índice antes del fetch para evitar condición de carrera con envíos concurrentes
    const eventIndex = this.eventLog.length - 1;
    const url = `${this.endpoint}`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    })
      .then(res => {
        this.eventLog[eventIndex].status = `enviado (${res.status})`;
      })
      .catch(err => {
        this.eventLog[eventIndex].status = 'error: ' + err.message;
      });
  }

  private _probePromise: Promise<string> | null = null;
  private _probeExpiry: number | null = null;

  public probeCausaRed(cb: (causa: string) => void): void {
    if (this._probePromise && this._probeExpiry !== null && Date.now() > this._probeExpiry) {
      this._probePromise = null;
      this._probeExpiry = null;
    }
    if (!this._probePromise) {
      this._probeExpiry = Date.now() + 30000;
      this._probePromise = fetch('/favicon.ico', { cache: 'no-store' })
        .then(() => 'servidor' as string)
        .catch(() => 'usuario' as string);
    }
    this._probePromise.then(cb);
  }

  public updateContext(lang?: string, currency?: string): void {
    if (lang) this.lang = lang;
    if (currency) this.currency = currency;
  }

  public getContext(): any {
    return { sessionId: this.sessionId, lang: this.lang, currency: this.currency };
  }

  /**
   * Imprime todos los eventos enviados en esta sesión
   * Llamar desde consola: telemetry.printEventLog()
   */
  public printEventLog(): void {

    if (this.eventLog.length === 0) {
      console.log('%cNo hay eventos registrados aún', 'color: #999; font-style: italic;');
      return;
    }

    // Tabla con eventos
    const tableData = this.eventLog.map(e => ({
      '#': e.num,
      'Evento': e.event,
      'Timestamp': e.timestamp.substring(11, 19), // HH:MM:SS
      'Status': e.status,
      'Data': JSON.stringify(e.data)
    }));

    console.table(tableData);
  }

  /**
   * Retorna el array de eventos para usar en código
   */
  public getEventLog(): any[] {
    return this.eventLog;
  }
}

export const telemetry = TelemetryClient.getInstance();
